import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Sparkles, Mic, Crown, Star, AlertTriangle } from "lucide-react";
import { CV_TEMPLATES } from "@/lib/cv-templates";
import { MiniPreview } from "@/components/cv/CVPreview";
import { Disclaimer } from "@/components/site/Disclaimer";


export const Route = createFileRoute("/cv-services")({
  head: () => ({
    meta: [
      { title: "AI CV Building & Interview Coaching — Nkadimeng Danny" },
      {
        name: "description",
        content:
          "Modern ATS-friendly CV templates, AI-tailored CV writing, and 1-on-1 interview coaching for South African job seekers. Free and premium templates available.",
      },
      { property: "og:title", content: "AI CV Building & Interview Coaching — Nkadimeng Danny" },
      {
        property: "og:description",
        content:
          "Get a recruiter-ready CV in minutes. AI tailors your CV to the target role and we coach you through the interview.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/cv-services" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/cv-services" }],
  }),
  component: CVServicesPage,
});

function CVServicesPage() {
  const free = CV_TEMPLATES.filter((t) => t.tier === "free");
  const premium = CV_TEMPLATES.filter((t) => t.tier === "premium");

  return (
    <div className="container-narrow py-12 space-y-6">
      <Disclaimer variant="compact" />
      <header className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">
          Career services
        </p>
        <h1 className="mt-2 font-display text-4xl text-navy">
          AI-built CVs &amp; Interview Coaching
        </h1>
        <p className="mt-3 text-foreground/80">
          Tell our AI about your background and target role — it constructs a
          modern, ATS-friendly CV tailored to that job, in your chosen template.
          Pair it with 1-on-1 interview coaching to walk in prepared.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to={"/cv-builder" as any}
            className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2.5 text-sm font-medium text-navy-foreground hover:bg-navy/90"
          >
            <Sparkles className="h-4 w-4" /> Build my CV free with AI
          </Link>
          <Link
            to="/book"
            className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2.5 text-sm hover:border-navy"
          >
            <Mic className="h-4 w-4" /> Book interview coaching
          </Link>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          No sign-up needed — 2 free AI generations per browser, then premium CVs from R15 (pricing varies by template quality and recruiter demand).
        </p>
      </header>

      <section className="mt-10 grid gap-4 md:grid-cols-3">
        <Pack
          title="CV Draft (Free)"
          price="R0"
          features={[
            "AI-generated CV from your inputs",
            "Choose any free template",
            "Download as PDF (print to PDF)",
            "ATS-friendly formatting",
          ]}
        />
        <Pack
          title="Tailored CV + Cover Letter"
          price="R350"
          highlight
          features={[
            "Role-specific keyword optimisation",
            "Premium template of choice",
            "Matching cover letter draft",
            "Human review by our team (24h)",
          ]}
        />
        <Pack
          title="Career Combo"
          price="R750"
          features={[
            "Everything in Tailored CV",
            "60-min interview coaching session",
            "Mock interview + feedback",
            "LinkedIn headline & summary copy",
          ]}
        />
      </section>

      <TemplateGrid
        title="Free templates"
        subtitle="Pick one and start building immediately."
        templates={free}
      />

      <TemplateGrid
        title="Premium templates"
        subtitle="Higher hiring probability — designed with recruiter eye-tracking studies in mind. Unlocked with any paid CV pack."
        templates={premium}
      />

      <section className="mt-14 rounded-lg border border-gold/40 bg-gold/5 p-6">
        <div className="flex flex-wrap items-start gap-4">
          <Mic className="mt-1 h-6 w-6 text-navy" />
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-xl text-navy">
              Interview coaching that lands offers
            </h3>
            <p className="mt-2 text-sm text-foreground/80">
              We run 60-minute structured sessions: STAR-method answers,
              behavioural questions, salary negotiation, and a recorded mock
              interview with written feedback. Suitable for graduates and
              experienced professionals across South Africa.
            </p>
            <Link
              to="/book"
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90"
            >
              Book a coaching slot
            </Link>
          </div>
        </div>
      </section>

      <p className="mt-8 text-xs text-muted-foreground">
        We help you present yourself professionally. We do not impersonate
        candidates, write false credentials, or guarantee job offers. CV
        writing and interview coaching are career-support services and do not
        constitute legal or financial advice.
      </p>
    </div>
  );
}

function Pack({
  title,
  price,
  features,
  highlight,
}: {
  title: string;
  price: string;
  features: string[];
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-5 ${
        highlight ? "border-navy bg-navy/5 shadow-soft" : "border-border bg-card"
      }`}
    >
      <p className="text-xs uppercase tracking-widest text-muted-foreground">
        {title}
      </p>
      <p className="mt-1 font-display text-3xl text-navy">{price}</p>
      <ul className="mt-3 space-y-1.5 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TemplateGrid({
  title,
  subtitle,
  templates,
}: {
  title: string;
  subtitle: string;
  templates: typeof CV_TEMPLATES;
}) {
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl text-navy">{title}</h2>
      <p className="mt-1 text-sm text-foreground/70">{subtitle}</p>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((t) => (
          <li
            key={t.id}
            className="group overflow-hidden rounded-lg border border-border bg-card transition hover:border-navy hover:shadow-soft"
          >
            <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-3">
              <MiniPreview template={t} />
              {t.tier === "premium" && (
                <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded bg-navy/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white shadow">
                  <Crown className="h-3 w-3" /> Premium
                </span>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-navy">{t.name}</h3>
                <span
                  className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-700"
                  title="Estimated ATS pass-through + recruiter shortlist probability"
                >
                  <Star className="h-3 w-3 fill-current" /> {t.hireScore}%
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{t.bestFor}</p>
              <p className="mt-2 text-sm text-foreground/80">{t.blurb}</p>
              {t.avoidIf && (
                <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700">
                  <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                  <span>{t.avoidIf}</span>
                </p>
              )}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-sm font-medium text-navy">
                  {t.tier === "free" ? "Free" : `R${t.price}`}
                </span>
                <Link
                  to={"/cv-builder" as any}
                  search={{ template: t.id } as any}
                  className="text-xs font-medium text-navy hover:underline"
                >
                  Use template →
                </Link>
              </div>
            </div>
          </li>

        ))}
      </ul>
    </section>
  );
}
